/**
 * MifeLife v2 — Session room extras.
 *   - AddToCalendar: Google / Outlook / .ics from /v2/sessions/:id/calendar
 *   - GlobalMentoringBeta: low-cost live caption PoC (mic → Whisper → translate)
 *
 * Both are fully isolated: if anything errors, the Jitsi call is unaffected.
 */
import React, { useEffect, useRef, useState } from 'react';
import { CalendarPlus, Globe, Loader2, Mic, MicOff, ChevronDown } from 'lucide-react';
import { API_URL, getToken, apiRequest } from '../../lib/utils';

// --------------------------------------------------------------------------
export const AddToCalendar = ({ sessionId, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (data) return;
    setLoading(true);
    try {
      const d = await apiRequest(`/v2/sessions/${sessionId}/calendar`);
      setData(d);
    } catch (_) { /* silent — calendar is non-critical */ }
    finally { setLoading(false); }
  };

  const downloadIcs = () => {
    if (!data?.ics) return;
    const blob = new Blob([data.ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `mifelife-gorusme.ics`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="relative">
      <button
        onClick={() => { setOpen((o) => !o); load(); }}
        className={`inline-flex items-center gap-1.5 ${compact ? 'h-8 px-3 text-xs' : 'h-9 px-4 text-sm'} rounded-lg border border-white/15 text-slate-200 hover:bg-white/[0.04] transition`}
        data-testid="v2-add-to-calendar"
      >
        <CalendarPlus className="w-3.5 h-3.5" /> Takvime ekle <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-48 rounded-xl border border-white/10 bg-[#16162A] shadow-2xl p-1.5 z-30" data-testid="v2-calendar-menu">
          {loading && <div className="px-3 py-2 text-xs text-slate-400 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Yükleniyor…</div>}
          {data && (
            <>
              <a href={data.google_url} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-white/[0.06] transition" data-testid="v2-cal-google">Google Takvim</a>
              <a href={data.outlook_url} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-white/[0.06] transition" data-testid="v2-cal-outlook">Outlook</a>
              <button onClick={downloadIcs} className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-white/[0.06] transition" data-testid="v2-cal-ics">.ics indir (Apple/Diğer)</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// --------------------------------------------------------------------------
const CLIP_MS = 4000;

export const GlobalMentoringBeta = ({ defaultSource = 'tr' }) => {
  const [cfg, setCfg] = useState(null);
  const [myLang, setMyLang] = useState(defaultSource);
  const [otherLang, setOtherLang] = useState('en');
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [captions, setCaptions] = useState([]);
  const [note, setNote] = useState(null);

  const activeRef = useRef(false);
  const streamRef = useRef(null);

  useEffect(() => {
    apiRequest('/v2/translate/config').then(setCfg).catch(() => {});
    return () => { activeRef.current = false; stopStream(); };
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  // Records one ~4s clip, returns a Blob.
  const recordClip = (stream) => new Promise((resolve) => {
    let mime = 'audio/webm';
    if (!window.MediaRecorder || !MediaRecorder.isTypeSupported(mime)) mime = '';
    const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    mr.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    mr.onstop = () => resolve(new Blob(chunks, { type: mr.mimeType || 'audio/webm' }));
    mr.start();
    setTimeout(() => { try { mr.stop(); } catch (_) {} }, CLIP_MS);
  });

  const sendClip = async (blob) => {
    const fd = new FormData();
    fd.append('file', blob, 'chunk.webm');
    fd.append('source_lang', myLang);
    fd.append('target_lang', otherLang);
    const res = await fetch(`${API_URL}/v2/translate/caption`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: fd,
    });
    if (!res.ok) throw new Error('caption failed');
    return res.json();
  };

  const loop = async (stream) => {
    while (activeRef.current) {
      try {
        const blob = await recordClip(stream);
        if (!activeRef.current) break;
        const { source_text, translation } = await sendClip(blob);
        if (source_text && translation) {
          setCaptions((c) => [...c.slice(-5), { id: Date.now(), src: source_text, dst: translation }]);
          setNote(null);
        }
      } catch (_) {
        setNote('Çeviri geçici olarak duraklatıldı; görüşme devam ediyor.');
      }
    }
  };

  const start = async () => {
    if ([myLang, otherLang].some((x) => x === '') || myLang === otherLang) return;
    setBusy(true); setNote(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      activeRef.current = true; setActive(true);
      loop(stream);
    } catch (_) {
      setNote('Mikrofon izni verilmedi. Çeviri başlatılamadı.');
    } finally { setBusy(false); }
  };

  const stop = () => {
    activeRef.current = false; setActive(false);
    stopStream();
  };

  if (!cfg || !cfg.enabled) return null;
  const langs = cfg.languages || { tr: 'Türkçe', en: 'English', de: 'Deutsch' };
  const opts = Object.keys(langs);

  return (
    <div className="rounded-2xl border border-violet-400/20 bg-gradient-to-br from-violet-500/[0.06] to-fuchsia-500/[0.03] p-5" data-testid="v2-global-mentoring-beta">
      <div className="flex items-center gap-2 mb-1">
        <Globe className="w-4 h-4 text-fuchsia-300" />
        <span className="text-sm font-semibold tracking-tight">Global Mentoring</span>
        <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/30 text-fuchsia-200">Beta</span>
      </div>
      <p className="text-xs text-slate-400 mb-4">Farklı dilde görüşme için canlı çeviri altyazısı. (Mikrofon tabanlı, deneysel.)</p>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Ben konuşuyorum</span>
          <select value={myLang} onChange={(e) => setMyLang(e.target.value)} disabled={active}
            className="mt-1 w-full h-9 rounded-lg bg-[#0a0a14] border border-white/15 text-sm text-white px-2 focus:outline-none focus:border-violet-400/60 disabled:opacity-50" data-testid="v2-beta-my-lang">
            {opts.map((k) => <option key={k} value={k}>{langs[k]}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Karşı taraf</span>
          <select value={otherLang} onChange={(e) => setOtherLang(e.target.value)} disabled={active}
            className="mt-1 w-full h-9 rounded-lg bg-[#0a0a14] border border-white/15 text-sm text-white px-2 focus:outline-none focus:border-violet-400/60 disabled:opacity-50" data-testid="v2-beta-other-lang">
            {opts.map((k) => <option key={k} value={k}>{langs[k]}</option>)}
          </select>
        </label>
      </div>

      {myLang === otherLang && <p className="text-[11px] text-amber-300 mb-2">İki farklı dil seç.</p>}

      <button
        onClick={active ? stop : start}
        disabled={busy || myLang === otherLang}
        className={`w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-semibold transition disabled:opacity-50 ${active ? 'bg-rose-500/90 hover:bg-rose-500 text-white' : 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white'}`}
        data-testid="v2-beta-toggle"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : active ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        {active ? 'Canlı çeviriyi durdur' : 'Canlı çeviriyi başlat'}
      </button>

      {note && <p className="mt-2 text-[11px] text-amber-300" data-testid="v2-beta-note">{note}</p>}

      {captions.length > 0 && (
        <div className="mt-4 space-y-2 max-h-48 overflow-y-auto" data-testid="v2-beta-captions">
          {captions.map((c) => (
            <div key={c.id} className="rounded-lg bg-[#0a0a14] border border-white/[0.06] px-3 py-2">
              <div className="text-[12px] text-slate-400">{c.src}</div>
              <div className="text-sm text-fuchsia-200 mt-0.5">{c.dst}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
